// Resultados de la IA (por omisión Claude Sonnet 5.5 por API de Anthropic, o el cliente simulado mientras no haya llave).
import { z } from 'zod';
import { Fecha, FechaHora, Id, Medidor, Tarifa, Texto } from './basicos.ts';
import { Aviso } from './avisos.ts';
import { Lecturas } from './recibo.ts';
import { EdicionCampo } from './edicion.ts';
import { IDS_MODELOS_IA, MODELO_IA, MODELOS_CASCADA, MODELOS_IA } from '../constantes.ts';
import type { IdModeloIA, PrecioModeloIA } from '../constantes.ts';

/** real = hay llave y se llama a la API; simulado = accesorios de prueba (se ve en la interfaz); sin_llave = la IA no
 *  está disponible y cada llamada TRUENA con IA_NO_DISPONIBLE (el vendedor puede llenar los datos a mano). */
export const ModoIA = z.enum(['real', 'simulado', 'sin_llave']);
export type ModoIA = z.infer<typeof ModoIA>;

/** Modelo de IA configurable (contrato v2): uno de MODELOS_IA (constantes.ts), cada uno con su precio. Por omisión
 *  MODELO_IA (claude-sonnet-5-5 desde el 12-oct-2026, decisión de Diego). Un modelo fuera de la lista no pasa: sin precio
 *  no hay tope. */
export const ModeloIA = z.enum(IDS_MODELOS_IA);
export type ModeloIA = z.infer<typeof ModeloIA>;

/** De dónde salió el modelo vigente: omision = MODELO_IA; env = IA_MODELO de .env; pantalla = Configuración (manda). */
export const OrigenModeloIA = z.enum(['omision', 'env', 'pantalla']);
export type OrigenModeloIA = z.infer<typeof OrigenModeloIA>;

/** Cascada para leer recibos (constantes.ts → MODELOS_CASCADA): APAGADA por omisión. Diego la enciende en Configuración → IA. */
export const CascadaIA = z.strictObject({
  activa: z.boolean(),
  /** El modelo barato que lee primero. Lo que no cuadra o trae duda se vuelve a leer con Opus. */
  modelo_barato: z.enum(MODELOS_CASCADA),
});
export type CascadaIA = z.infer<typeof CascadaIA>;

export const EstadoIA = z.strictObject({
  modo: ModoIA,
  /** Modelo con el que se llamará a la API (antes era el literal claude-opus-5-5; por omisión, MODELO_IA). */
  modelo: ModeloIA,
  /** Opcional mientras el servidor se migra; el servidor v2 lo manda siempre. */
  modelo_origen: OrigenModeloIA.optional(),
  /** De dónde salió la llave: .env manda sobre la pantalla de configuración. Nube (tanda 2): `secret_manager` = Cloud Run la
   *  inyecta desde Secret Manager (secreto anthropic-api-key, versión fija); ahí no hay .env ni se pega en la pantalla. */
  llave_origen: z.enum(['env', 'pantalla', 'secret_manager']).nullable(),
  /** Nube, tanda 2: true = en este servidor la llave SOLO vive en Secret Manager; la pantalla lo dice en vez de ofrecer
   *  pegarla (y el servidor rechaza pegarla o borrarla). Ausente = false. */
  llave_secret_manager: z.boolean().optional(),
  /** Últimos 4 caracteres, para reconocerla. La llave completa NUNCA sale del servidor. */
  llave_final: z.string().length(4).nullable(),
  gasto_mes_usd: z.number().nonnegative(),
  tope_mes_usd: z.number().positive().nullable(),
  mensaje: z.string().max(300),
  /** 12-oct · la cascada de la lectura (apagada por omisión). Ausente = servidor de antes (sin cascada). */
  cascada: CascadaIA.optional(),
});
export type EstadoIA = z.infer<typeof EstadoIA>;

/** Lo que ve de la IA quien NO es director (GET /api/yo): solo lo que necesita la franja y el botón de leer. El origen
 *  y los últimos 4 de la llave, el gasto y el tope quedan detrás de configurar_ia (hallazgo del 10-oct). */
export const EstadoIAPublico = EstadoIA.pick({ modo: true, modelo: true, mensaje: true });
export type EstadoIAPublico = z.infer<typeof EstadoIAPublico>;

/** El estado de la IA sin lo que es solo del director. El mensaje tampoco lleva cifras de gasto. */
export function estadoIAPublico(e: EstadoIA): EstadoIAPublico {
  let mensaje: string;
  if (e.modo === 'simulado') mensaje = 'IA SIMULADA: las lecturas y revisiones son de prueba.';
  else if (e.modo === 'sin_llave') mensaje = 'La IA todavía no tiene llave: los recibos PDF de CFE se leen sin IA; las fotos y los escaneos se capturan a mano.';
  else if (/tope/i.test(e.mensaje)) mensaje = 'La IA llegó al tope de gasto del mes y está detenida: avísale a Diego.';
  else if (/no tiene la forma|no sirve|no va a funcionar/i.test(e.mensaje)) mensaje = 'La llave de la IA no sirve: avísale a Diego.';
  else mensaje = 'La IA está lista.';
  return { modo: e.modo, modelo: e.modelo, mensaje };
}

export const OperacionIA = z.enum(['leer_recibos', 'corregir', 'revisar_rentabilidad']);
export type OperacionIA = z.infer<typeof OperacionIA>;

export const UsoIA = z.strictObject({
  operacion: OperacionIA,
  modelo: Texto(2, 60),
  tokens_entrada: z.int().nonnegative(),
  tokens_salida: z.int().nonnegative(),
  tokens_cache_lectura: z.int().nonnegative(),
  tokens_cache_escritura: z.int().nonnegative(),
  costo_usd: z.number().nonnegative(),
  simulado: z.boolean(),
  en: FechaHora,
  cotizacion_id: Id.nullable(),
  usuario_id: Id.nullable(),
});
export type UsoIA = z.infer<typeof UsoIA>;

/** Costo estimado en USD de una llamada, con los precios de MODELOS_IA (constantes.ts). Sin modelo: MODELO_IA.
 *  Un modelo de respaldo que no está en la lista lo cobra paquetes/ia con su propia tabla (al precio más caro). */
export function costoUsd(
  t: Pick<UsoIA, 'tokens_entrada' | 'tokens_salida' | 'tokens_cache_lectura' | 'tokens_cache_escritura'>,
  modelo: IdModeloIA = MODELO_IA,
): number {
  const m = MODELOS_IA[modelo];
  if (!m) throw new Error(`costoUsd: el modelo "${modelo}" no está en MODELOS_IA (constantes.ts): sin su precio no se puede cuidar el tope.`);
  const p = precioDeLlamada(m.precio, t);
  const usd =
    (t.tokens_entrada * p.entrada + t.tokens_salida * p.salida + t.tokens_cache_lectura * p.cache_lectura + t.tokens_cache_escritura * p.cache_escritura) /
    1_000_000;
  return Math.round(usd * 10_000) / 10_000;
}

/** La tarifa que aplica a UNA llamada: la larga si el modelo la tiene y la entrada de la petición (entrada + caché) pasa
 *  del umbral (Haiku 5.5: más de 100K tokens). */
export function precioDeLlamada(
  p: PrecioModeloIA,
  t: Pick<UsoIA, 'tokens_entrada' | 'tokens_cache_lectura' | 'tokens_cache_escritura'>,
): Omit<PrecioModeloIA, 'largo'> {
  const entrada = t.tokens_entrada + t.tokens_cache_lectura + t.tokens_cache_escritura;
  return p.largo && entrada > p.largo.mas_de_tokens_entrada ? p.largo : p;
}

// ───────────── Lista POR ARCHIVO de la lectura (pantalla "estado por archivo") ─────────────

/** Qué pasó con cada archivo que recibió la IA al leer recibos:
 *  leido / parcial / ilegible = como EstadoLectura, pero de ESE archivo;
 *  repetido = trae el mismo periodo de otro archivo del mismo medidor (repetido_de dice cuál; no se usa);
 *  no_es_cfe = no es un recibo de CFE; otro_servicio = es de CFE pero de otro número de servicio que el resto;
 *  no_leible_por_ia = formato que la API no lee y no se pudo convertir (HEIC sin convertidor, PDF dañado);
 *  sin_leer = no se leyó (p. ej. se pasó del tope de tamaño de la petición);
 *  fuera_del_anio = recibo leído pero más viejo que el año que se cotiza (los 12 meses que terminan en el recibo más
 *  reciente): no se usó y no cuenta en «N de 12» (Diego, 25-oct: al subir recibos nuevos, los viejos salen). */
export const ESTADOS_ARCHIVO_LEIDO = ['leido', 'parcial', 'ilegible', 'repetido', 'no_es_cfe', 'otro_servicio', 'no_leible_por_ia', 'sin_leer', 'fuera_del_anio'] as const;
export const EstadoArchivoLeido = z.enum(ESTADOS_ARCHIVO_LEIDO);
export type EstadoArchivoLeido = z.infer<typeof EstadoArchivoLeido>;

export const ArchivoLeido = z
  .strictObject({
    archivo_id: Id,
    medidor: Medidor,
    estado: EstadoArchivoLeido,
    /** Periodo facturado que trae ESE recibo (sirve para ver periodos encimados entre archivos). */
    periodo: z.strictObject({ inicio: Fecha, fin: Fecha }).nullable(),
    tarifa: Tarifa.nullable(),
    /** Solo con estado "repetido": el archivo que sí se usó para ese periodo. */
    repetido_de: Id.nullable(),
    /** Una línea para el vendedor: qué se leyó o por qué no sirve. */
    nota: z.string().trim().max(400).nullable(),
    /** Etapa 4: quién lo leyó: "reglas" = el lector SIN IA (PDF digital de CFE), "ia" = la IA real, "ia_simulada" = un
     *  accesorio de prueba. null = nadie lo leyó. Opcional: las listas guardadas antes de la etapa 4 no lo traen. */
    leido_por: z.enum(['reglas', 'ia', 'ia_simulada']).nullable().optional(),
  })
  .superRefine((a, ctx) => {
    if ((a.estado === 'repetido') !== (a.repetido_de !== null))
      ctx.addIssue({ code: 'custom', path: ['repetido_de'], message: 'repetido_de va solo (y siempre) con estado "repetido".' });
    if (a.repetido_de === a.archivo_id) ctx.addIssue({ code: 'custom', path: ['repetido_de'], message: 'Un archivo no puede ser repetido de sí mismo.' });
    if (a.periodo && a.periodo.inicio > a.periodo.fin) ctx.addIssue({ code: 'custom', path: ['periodo'], message: 'Periodo al revés.' });
  });
export type ArchivoLeido = z.infer<typeof ArchivoLeido>;

/** Problemas de una lista por archivo contra los archivos que SE MANDARON a leer (vacío = cuadra): cada archivo
 *  exactamente una vez, con su medidor, sin ids de más, y "repetido_de" apuntando a un archivo de la misma lista.
 *  La IA la usa en sus pruebas y el servidor la AFIRMA antes de guardar (lista que no cuadra = IA_FALLO, no se guarda). */
export function revisarArchivosLeidos(lista: readonly ArchivoLeido[], enviados: readonly { id: string; medidor: 'A' | 'B' }[]): string[] {
  const fallas: string[] = [];
  const porId = new Map<string, ArchivoLeido[]>();
  for (const a of lista) porId.set(a.archivo_id, [...(porId.get(a.archivo_id) ?? []), a]);
  for (const e of enviados) {
    const v = porId.get(e.id) ?? [];
    if (v.length === 0) fallas.push(`El archivo ${e.id} se mandó a leer y no trae estado.`);
    else if (v.length > 1) fallas.push(`El archivo ${e.id} viene ${v.length} veces en la lista.`);
    else if (v[0]!.medidor !== e.medidor) fallas.push(`El archivo ${e.id} es del medidor ${e.medidor} y la lista dice ${v[0]!.medidor}.`);
  }
  const ids = new Set(enviados.map((e) => e.id));
  for (const a of lista) {
    if (!ids.has(a.archivo_id)) fallas.push(`La lista trae el archivo ${a.archivo_id}, que no se mandó a leer.`);
    if (a.repetido_de !== null && !ids.has(a.repetido_de)) fallas.push(`El archivo ${a.archivo_id} dice ser repetido de ${a.repetido_de}, que no se mandó a leer.`);
  }
  return [...new Set(fallas)];
}

export const ResultadoLeerRecibos = z.strictObject({
  lecturas: Lecturas,
  avisos: z.array(Aviso),
  /** Lo que la IA entendió de la caja de notas libres y del cuestionario (para que el vendedor lo confirme). */
  contexto: z.array(z.string().min(3).max(400)).max(20),
  /** Contrato v2: un renglón por CADA archivo que se mandó a leer (revisarArchivosLeidos). Opcional solo mientras el
   *  paquete IA se migra; el servidor lo guarda en borrador.archivos_leidos. */
  archivos: z.array(ArchivoLeido).max(60).optional(),
  uso: UsoIA,
});
export type ResultadoLeerRecibos = z.infer<typeof ResultadoLeerRecibos>;

/** z25: un periodo del historial cuyo consumo la IA propone cambiar (posición en /lecturas/<medidor>/historial). */
export const MesAjustado = z.strictObject({
  indice: z.int().nonnegative(),
  etiqueta: z.string().min(1).max(40),
  antes_kwh: z.number().nonnegative(),
  despues_kwh: z.number().nonnegative(),
});
export type MesAjustado = z.infer<typeof MesAjustado>;

/** z25 (Diego, 20-oct-2026): «que la IA corrija y modifique los consumos según pida el que está cotizando». La propuesta
 *  de consumos nuevos, mes por mes, con por qué y sus supuestos; las ediciones que la aplican van en `ediciones`. */
export const AjusteConsumosPropuesto = z.strictObject({
  medidor: z.enum(['A', 'B']),
  justificacion: z.string().min(3).max(400),
  supuestos: z.array(z.string().min(3).max(300)).max(8),
  meses: z.array(MesAjustado).min(1).max(24),
});
export type AjusteConsumosPropuesto = z.infer<typeof AjusteConsumosPropuesto>;

export const ResultadoCorregir = z.strictObject({
  /** Propuesta: NO se aplica sola. La interfaz la muestra y el vendedor la aplica con PATCH .../borrador. */
  ediciones: z.array(EdicionCampo).max(60),
  explicacion: z.string().min(3).max(2_000),
  avisos: z.array(Aviso),
  /** z25: si la instrucción pide ajustar consumos, la tabla antes/después que ve el vendedor (sus ediciones van arriba). */
  ajuste_consumos: AjusteConsumosPropuesto.nullable().optional(),
  uso: UsoIA,
});
export type ResultadoCorregir = z.infer<typeof ResultadoCorregir>;

export const VeredictoRevision = z.enum(['bien', 'revisar', 'pierde_dinero']);
export type VeredictoRevision = z.infer<typeof VeredictoRevision>;

export const ResultadoRevision = z.strictObject({
  veredicto: VeredictoRevision,
  resumen: z.string().min(3).max(3_000),
  avisos: z.array(Aviso),
  uso: UsoIA,
});
export type ResultadoRevision = z.infer<typeof ResultadoRevision>;

// ───────────── hoy86 · ADENDA z62-2 + z64-2: «Sugerir con IA» de la Revisión ─────────────
// Diego (29→30-oct): «si la IA de verdad acierta más que la curva, SE PRENDE aunque cada uso cueste centavos de dólar (si falla,
// cae a la curva con aviso)». Lo que la web le pasa a la IA de UN cliente: tarifa, facturación, ciudad, los periodos reales con
// que estima la curva y los periodos del año por estimar (sus fechas y lo que da la curva de Sonora). Nada del titular.
// hoy86 (verificación r2): unas fechas al revés no son un periodo (el prompt le diría a la IA «-60 días»): 400.
const fechasEnOrden = (p: { inicio: string; fin: string }) => p.inicio <= p.fin;
export const PeriodoRealSugerir = z.strictObject({ inicio: Fecha, fin: Fecha, kwh: z.number().nonnegative().max(100_000_000) }).refine(fechasEnOrden, 'el periodo termina antes de empezar');
export type PeriodoRealSugerir = z.infer<typeof PeriodoRealSugerir>;
export const PeriodoSugerir = z.strictObject({ inicio: Fecha, fin: Fecha, curva: z.number().nonnegative().max(100_000_000) }).refine(fechasEnOrden, 'el periodo termina antes de empezar');
export type PeriodoSugerir = z.infer<typeof PeriodoSugerir>;
export const EntradaSugerirConsumos = z.strictObject({
  tarifa: Tarifa,
  facturacion: z.enum(['mensual', 'bimestral']),
  negocio: z.boolean(),
  ciudad: z.string().trim().max(60),
  reales: z.array(PeriodoRealSugerir).min(1).max(12),
  por_estimar: z.array(PeriodoSugerir).min(1).max(12),
});
export type EntradaSugerirConsumos = z.infer<typeof EntradaSugerirConsumos>;
/** Lo que contesta «Sugerir con IA». fuente «ia» (o «ia_simulada»): `kwh` trae un número entero por periodo de `por_estimar`, en
 *  su orden, ya comprobado (ni de más ni de menos, > 0 y dentro de lo creíble). fuente «curva»: la IA no sirvió (`motivo` dice
 *  por qué, para el aviso AMARILLO) y la web pone la curva de Sonora: `kwh` va vacío. */
export const ResultadoSugerirConsumos = z.discriminatedUnion('fuente', [
  z.strictObject({ fuente: z.enum(['ia', 'ia_simulada']), kwh: z.array(z.int().positive()).min(1).max(12), nota: z.string().max(400), uso: UsoIA }),
  z.strictObject({ fuente: z.literal('curva'), kwh: z.array(z.int()).max(0), motivo: z.string().min(3).max(300), uso: UsoIA.nullable() }),
]);
export type ResultadoSugerirConsumos = z.infer<typeof ResultadoSugerirConsumos>;

/** Lo que queda guardado en el borrador de la última revisión (los avisos van a borrador.avisos con origen ia_revision). */
export const RevisionIA = z.strictObject({
  veredicto: VeredictoRevision,
  resumen: z.string().min(3).max(3_000),
  revisado_en: FechaHora,
  simulada: z.boolean(),
});
export type RevisionIA = z.infer<typeof RevisionIA>;
